import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Sparkles, AlertCircle, Heart } from "lucide-react";
import { motion } from "framer-motion";
import { Trans, useTranslation } from "react-i18next";
import Header from "../components/Header";
import { checkBillingStatus, fetchUsage } from "../lib/api";
import { track } from "../lib/analytics";
import { trackPurchase } from "../lib/marketingPixels";

/**
 * LockInSuccess — the celebration page Stripe redirects to after a
 * successful /lock-in checkout. Polls /billing/status/{session_id}
 * until the webhook flips `processed=true`, then shows the founding
 * member welcome.
 *
 * Why a separate page (not just /billing/success):
 * The standard billing-success page is generic ("you're Pro for 30
 * days"). This one celebrates the founding-member moment with the
 * specific copy the founder asked for + the badge confirmation pulled
 * from /api/me/usage (founding_lock_in: true).
 */

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 10;

const LockInSuccess = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "checking" });
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: t("lockInSuccess.missingSession") });
      return;
    }
    track("lock_in_success_opened");
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await checkBillingStatus(sessionId);
        if (cancelled) return;
        if (res.payment_status === "paid" && res.processed) {
          // Pull usage to read founding_lock_in flag
          try {
            const u = await fetchUsage();
            if (!cancelled) setUsage(u);
          } catch {
            /* non-fatal — still show celebration */
          }
          if (!cancelled) {
            track("lock_in_success_confirmed", {
              founding_lock_in: usage?.founding_lock_in === true,
            });
            // TikTok/Taboola/GTM CompletePayment — $100 Founding
            // Lifetime one-time payment.
            const amountUsd =
              typeof res?.amount === "number" && res.amount > 0
                ? res.amount
                : 100;
            trackPurchase({
              value: amountUsd,
              currency: (res?.currency || "USD").toUpperCase(),
              content_id: "founding_lock_in",
              content_type: "lifetime_access",
              transaction_id: sessionId,
            });
            setState({ status: "success" });
          }
          return;
        }
        if (res.status === "expired") {
          setState({ status: "error", message: t("lockInSuccess.expired") });
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState({
            status: "pending",
            message: t("lockInSuccess.pendingMessage"),
          });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setState({
          status: "error",
          message: e?.response?.data?.detail || t("lockInSuccess.verifyError"),
        });
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const claimed = usage?.founding_lock_in === true;

  return (
    <div className="min-h-screen trubeaute-gradient">
      <Header />
      <div
        className="pt-32 sm:pt-40 px-6 max-w-2xl mx-auto text-center pb-20"
        data-testid="lock-in-success-page"
      >
        {state.status === "checking" && (
          <div data-testid="lock-in-checking">
            <Loader2 className="h-10 w-10 animate-spin mx-auto text-[#9B6A6C] mb-6" />
            <h1 className="text-3xl font-light text-[#2C2123] mb-3">
              {t("lockInSuccess.checkingTitle")}
            </h1>
            <p className="text-[#5C4B51]">{t("lockInSuccess.checkingBody")}</p>
          </div>
        )}

        {state.status === "success" && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            data-testid="lock-in-celebration"
          >
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-[#9B6A6C]/15 mb-5">
              <Sparkles className="h-8 w-8 text-[#9B6A6C]" />
            </div>
            <div className="text-xs uppercase tracking-[0.28em] text-[#9B6A6C] mb-3">
              {claimed ? t("lockInSuccess.badgeFounding") : t("lockInSuccess.badgeWelcomePro")}
            </div>
            <h1 className="font-serif text-[#2C2123] leading-tight text-4xl sm:text-5xl mb-5">
              {t("lockInSuccess.headlineBefore")}{" "}
              <span className="italic">{t("lockInSuccess.headlineEmphasis")}</span>.
            </h1>
            <p className="text-[#5C4B51] text-base sm:text-lg mb-8 leading-relaxed max-w-lg mx-auto">
              {claimed ? (
                <Trans
                  i18nKey="lockInSuccess.bodyFounding"
                  components={{ strong: <span className="font-medium text-[#2C2123]" /> }}
                />
              ) : (
                t("lockInSuccess.bodyAnnual")
              )}
            </p>

            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={() => navigate("/capture")}
                data-testid="lock-in-start-routine"
                className="bg-[#9B6A6C] hover:bg-[#5D4050] text-white px-7 py-3 rounded-full font-medium text-sm transition-colors inline-flex items-center gap-2"
              >
                <Heart className="h-4 w-4" />
                {t("lockInSuccess.startRoutine")}
              </button>
              <button
                onClick={() => navigate("/settings")}
                className="bg-white/70 hover:bg-white text-[#2C2123] border border-[#EADDD7] px-7 py-3 rounded-full font-medium text-sm transition-colors"
              >
                {t("lockInSuccess.settings")}
              </button>
            </div>

            {claimed && (
              <div className="mt-10 inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-[#EADDD7] rounded-full px-4 py-2">
                <Sparkles className="h-3.5 w-3.5 text-[#9B6A6C]" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-[#5D4050]">
                  {t("lockInSuccess.confirmed")}
                </span>
              </div>
            )}
          </motion.div>
        )}

        {state.status === "pending" && (
          <div data-testid="lock-in-pending">
            <Loader2 className="h-10 w-10 animate-spin mx-auto text-[#9B6A6C] mb-6" />
            <h1 className="text-2xl font-light text-[#2C2123] mb-3">{t("lockInSuccess.almostThere")}</h1>
            <p className="text-[#5C4B51] mb-6">{state.message}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-[#9B6A6C] hover:bg-[#5D4050] text-white px-7 py-2.5 rounded-full font-medium text-sm transition-colors"
            >
              {t("lockInSuccess.refresh")}
            </button>
          </div>
        )}

        {state.status === "error" && (
          <div data-testid="lock-in-error">
            <AlertCircle className="h-10 w-10 mx-auto text-[#9B6A6C] mb-6" />
            <h1 className="text-2xl font-light text-[#2C2123] mb-3">
              {t("lockInSuccess.errorTitle")}
            </h1>
            <p className="text-[#5C4B51] mb-6">{state.message}</p>
            <button
              onClick={() => navigate("/lock-in")}
              className="bg-[#9B6A6C] hover:bg-[#5D4050] text-white px-7 py-2.5 rounded-full font-medium text-sm transition-colors"
            >
              {t("lockInSuccess.tryAgain")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LockInSuccess;
